import type {
  AnnualScenarioImpact,
  FinanceData,
  FinancialLine,
  MonthlyResult,
  PersonnelRecord,
  ScenarioInputs,
  ScenarioResult,
  ScenarioSummary,
} from './types';
import { clamp } from './format';

const sumLines = (lines: FinancialLine[], month: string) =>
  lines.reduce((total, line) => total + (line.amounts[month] ?? 0), 0);

const forecastMonths = (data: FinanceData) =>
  data.months.filter((month) => month.kind === 'forecast');

const PERSONNEL_LEDGER_LINE_IDS = new Set([
  'exp-r4-salaries-verocity',
  'exp-r5-salaries-user-success',
  'exp-r8-salaries-management',
  'exp-r9-bonus-accrual-for-non-sales-employees',
  'exp-r12-payroll-taxes-medicare',
  'exp-r13-payroll-taxes-social-security',
  'exp-r18-health-insurance',
]);

export const isPersonnelLedgerLine = (lineId: string) =>
  PERSONNEL_LEDGER_LINE_IDS.has(lineId);

export const createBaselineScenario = (data: FinanceData): ScenarioInputs => ({
  name: 'Working scenario',
  expenseAdjustments: {},
  revenueAdjustments: {},
  personnelAdjustments: {},
  clientRevenueAdjustments: {},
  ownerDrawMonthly: data.baselineMonthlyOwnerDraw,
  arRecoveryPercent: 0,
  arRecoveryMonths: 3,
  debtPrepayment: 0,
});

const appliesBeginning = (month: string, startMonth?: string) =>
  !startMonth || month >= startMonth;

export const personnelPlanCost = (
  data: FinanceData,
  record: PersonnelRecord,
  month: string,
) => {
  const amount = record.amounts[month] ?? 0;
  if (record.costScope === 'total_employer_cost' || amount <= 0) return amount;
  const assumptions = data.personnelCostAssumptions;
  return amount +
    amount * Math.max(0, assumptions?.employerPayrollTaxRate ?? 0) +
    amount * Math.max(0, assumptions?.bonusAccrualRate ?? 0) +
    Math.max(0, assumptions?.monthlyHealthInsurance ?? 0);
};

const severanceSalary = (
  data: FinanceData,
  record: PersonnelRecord,
  terminationMonth: string,
) => {
  const priorMonths = data.months
    .filter(({ month }) => month <= terminationMonth && (record.amounts[month] ?? 0) > 0);
  const prior = priorMonths[priorMonths.length - 1];
  const fallback = data.months.find(({ month }) => (record.amounts[month] ?? 0) > 0);
  return record.amounts[prior?.month ?? fallback?.month ?? ''] ?? 0;
};

export const personnelScenarioCost = (
  data: FinanceData,
  record: PersonnelRecord,
  inputs: ScenarioInputs,
  month: string,
) => {
  const monthRecord = data.months.find((entry) => entry.month === month);
  const baseline = personnelPlanCost(data, record, month);
  const adjustment = inputs.personnelAdjustments?.[record.id];
  if (!adjustment?.enabled || monthRecord?.kind !== 'forecast' || month < adjustment.terminationMonth) {
    return baseline;
  }
  if (month !== adjustment.terminationMonth) return 0;
  const severancePayrollTax = record.costScope === 'total_employer_cost'
    ? 0
    : Math.max(0, data.personnelCostAssumptions?.employerPayrollTaxRate ?? 0);
  return severanceSalary(data, record, adjustment.terminationMonth) *
    clamp(adjustment.severanceMonths, 0, 24) *
    (1 + severancePayrollTax);
};

export const personnelCostTotal = (
  data: FinanceData,
  record: PersonnelRecord,
  inputs?: ScenarioInputs,
  year?: string,
) => data.months
  .filter(({ month }) => !year || month.startsWith(`${year}-`))
  .reduce((total, { month }) => total + (inputs
    ? personnelScenarioCost(data, record, inputs, month)
    : personnelPlanCost(data, record, month)), 0);

export const calculateScenario = (
  data: FinanceData,
  inputs: ScenarioInputs,
): ScenarioResult => {
  const months = data.months;
  const forecast = forecastMonths(data);
  const recoverableAr = data.arAging
    .filter((bucket) => bucket.recoverable)
    .reduce((total, bucket) => total + Math.max(0, bucket.amount), 0);
  const totalArRecovery = recoverableAr * clamp(inputs.arRecoveryPercent, 0, 100) / 100;
  const recoveryMonths = Math.max(
    1,
    Math.min(Math.round(inputs.arRecoveryMonths || 1), Math.max(forecast.length, 1)),
  );
  const monthlyRecovery = totalArRecovery / recoveryMonths;
  const hasPersonnelAdjustments = Object.values(inputs.personnelAdjustments ?? {}).some(({ enabled }) => enabled);
  const hasClientAdjustments = Object.values(inputs.clientRevenueAdjustments ?? {}).some(({ enabled }) => enabled);

  let baselineCash = data.startingForecastCash;
  let scenarioCash = data.startingForecastCash;
  let firstForecastSeen = false;

  const results: MonthlyResult[] = months.map((monthRecord) => {
    const baselineRevenue = sumLines(data.revenueLines, monthRecord.month);
    const baselineExpenses = sumLines(data.expenseLines, monthRecord.month);
    const editable = monthRecord.kind === 'forecast';

    const scenarioRevenue = editable
      ? data.revenueLines.reduce((total, line) => {
          const adjustment = hasClientAdjustments ? undefined : inputs.revenueAdjustments[line.id];
          const multiplier = adjustment?.enabled && appliesBeginning(monthRecord.month, adjustment.startMonth)
            ? 1 + clamp(adjustment.changePercent, -100, 300) / 100
            : 1;
          return total + (line.amounts[monthRecord.month] ?? 0) * multiplier;
        }, 0)
      : baselineRevenue;

    const lineScenarioExpenses = editable
      ? data.expenseLines.reduce((total, line) => {
          const adjustment = hasPersonnelAdjustments && isPersonnelLedgerLine(line.id)
            ? undefined
            : inputs.expenseAdjustments[line.id];
          const multiplier = adjustment?.enabled && appliesBeginning(monthRecord.month, adjustment.startMonth)
            ? 1 - clamp(adjustment.cutPercent, 0, 100) / 100
            : 1;
          return total + (line.amounts[monthRecord.month] ?? 0) * multiplier;
        }, 0)
      : baselineExpenses;

    const scenarioPersonnelExpenseImpact = editable
      ? (data.personnel ?? []).reduce((total, record) => total +
          personnelScenarioCost(data, record, inputs, monthRecord.month) -
          personnelPlanCost(data, record, monthRecord.month), 0)
      : 0;
    const scenarioClientRevenueImpact = editable
      ? (data.clientRevenue ?? []).reduce((total, client) => {
          const adjustment = inputs.clientRevenueAdjustments?.[client.id];
          if (!adjustment?.enabled || !appliesBeginning(monthRecord.month, adjustment.startMonth)) return total;
          const baseline = client.amounts[monthRecord.month] ?? client.baselineMonthlyRevenue;
          return total + baseline * clamp(adjustment.changePercent, -100, 300) / 100;
        }, 0)
      : 0;
    const scenarioRevenueWithClients = scenarioRevenue + scenarioClientRevenueImpact;
    const scenarioExpenses = lineScenarioExpenses + scenarioPersonnelExpenseImpact;

    const baselineProfit = baselineRevenue - baselineExpenses;
    const scenarioProfit = scenarioRevenueWithClients - scenarioExpenses;
    let baselineEndingCash: number | null = null;
    let scenarioEndingCash: number | null = null;
    let scenarioArRecovery = 0;
    let scenarioDebtPrepayment = 0;

    if (editable) {
      const forecastIndex = forecast.findIndex((entry) => entry.month === monthRecord.month);
      const otherCashFlow = data.baselineOtherCashFlow[monthRecord.month] ?? 0;
      const governedDebtPayment = data.governedCashFlowAdjustments?.debtPayments?.[monthRecord.month] ?? 0;
      const scheduledPrincipal = governedDebtPayment > 0
        ? 0
        : data.debts.reduce(
            (total, debt) => total + debt.scheduledMonthlyPrincipal,
            0,
          );
      const baselineCashChange =
        baselineProfit +
        otherCashFlow -
        data.baselineMonthlyOwnerDraw -
        scheduledPrincipal;
      baselineCash += baselineCashChange;

      scenarioArRecovery = forecastIndex >= 0 && forecastIndex < recoveryMonths
        ? monthlyRecovery
        : 0;
      scenarioDebtPrepayment = !firstForecastSeen
        ? clamp(inputs.debtPrepayment, 0, data.debts.reduce((sum, debt) => sum + debt.balance, 0))
        : 0;
      const scenarioCashChange =
        scenarioProfit +
        otherCashFlow -
        Math.max(0, inputs.ownerDrawMonthly) -
        scheduledPrincipal +
        scenarioArRecovery -
        scenarioDebtPrepayment;
      scenarioCash += scenarioCashChange;
      baselineEndingCash = baselineCash;
      scenarioEndingCash = scenarioCash;
      firstForecastSeen = true;
    }

    return {
      month: monthRecord.month,
      kind: monthRecord.kind,
      baselineRevenue,
      scenarioRevenue: scenarioRevenueWithClients,
      baselineExpenses,
      scenarioExpenses,
      baselineProfit,
      scenarioProfit,
      baselineEndingCash,
      scenarioEndingCash,
      scenarioArRecovery,
      scenarioDebtPrepayment,
      scenarioPersonnelExpenseImpact,
      scenarioClientRevenueImpact,
    };
  });

  return {
    months: results,
    baseline: summarize(results, 'baseline', totalArRecovery),
    scenario: summarize(results, 'scenario', totalArRecovery),
    conflictsResolved: [
      ...(hasPersonnelAdjustments && Object.entries(inputs.expenseAdjustments).some(([id, adjustment]) =>
        adjustment.enabled && isPersonnelLedgerLine(id))
        ? ['Salary, payroll-tax, bonus, and health-insurance line cuts were ignored because employee-level adjustments already model those costs.']
        : []),
      ...(hasClientAdjustments && Object.values(inputs.revenueAdjustments).some(({ enabled }) => enabled)
        ? ['Revenue-stream changes were ignored because named-customer adjustments are active.']
        : []),
    ],
  };
};

export const summarizePeriod = (
  months: MonthlyResult[],
  mode: 'baseline' | 'scenario',
  scenarioRecovery = months.reduce((sum, month) => sum + month.scenarioArRecovery, 0),
  forecastOnly = false,
): ScenarioSummary => {
  const period = forecastOnly ? months.filter((month) => month.kind === 'forecast') : months;
  const revenue = period.reduce(
    (sum, month) => sum + (mode === 'baseline' ? month.baselineRevenue : month.scenarioRevenue),
    0,
  );
  const expenses = period.reduce(
    (sum, month) => sum + (mode === 'baseline' ? month.baselineExpenses : month.scenarioExpenses),
    0,
  );
  const baselineRevenue = period.reduce((sum, month) => sum + month.baselineRevenue, 0);
  const baselineExpenses = period.reduce((sum, month) => sum + month.baselineExpenses, 0);
  const cashValues = period
    .map((month) => mode === 'baseline' ? month.baselineEndingCash : month.scenarioEndingCash)
    .filter((value): value is number => value !== null);
  const firstNegative = period.find((month) => {
    const value = mode === 'baseline' ? month.baselineEndingCash : month.scenarioEndingCash;
    return value !== null && value < 0;
  });

  return {
    revenue,
    expenses,
    profit: revenue - expenses,
    endingCash: cashValues.length ? cashValues[cashValues.length - 1] : 0,
    minimumCash: cashValues.length ? Math.min(...cashValues) : 0,
    expenseSavings: baselineExpenses - expenses,
    incrementalRevenue: revenue - baselineRevenue,
    arRecovery: mode === 'scenario' ? scenarioRecovery : 0,
    runwayBreachMonth: firstNegative?.month ?? null,
  };
};

const summarize = (
  months: MonthlyResult[],
  mode: 'baseline' | 'scenario',
  scenarioRecovery: number,
) => summarizePeriod(months, mode, scenarioRecovery, true);

export const annualizedLineTotal = (
  data: FinanceData,
  line: FinancialLine,
  scenario?: ScenarioInputs,
  year?: string,
) =>
  data.months
    .filter((month) => year ? month.month.startsWith(`${year}-`) : month.kind === 'forecast')
    .reduce((total, month) => {
      const baseline = line.amounts[month.month] ?? 0;
      if (!scenario) return total + baseline;
      const expense = scenario.expenseAdjustments[line.id];
      if (month.kind === 'forecast' && expense?.enabled && appliesBeginning(month.month, expense.startMonth)) {
        return total + baseline * (1 - clamp(expense.cutPercent, 0, 100) / 100);
      }
      const revenue = scenario.revenueAdjustments[line.id];
      if (month.kind === 'forecast' && revenue?.enabled && appliesBeginning(month.month, revenue.startMonth)) {
        return total + baseline * (1 + clamp(revenue.changePercent, -100, 300) / 100);
      }
      return total + baseline;
    }, 0);

export const aggregateAnnualScenario = (
  months: MonthlyResult[],
  years: readonly string[] = [...new Set(months.map((month) => month.month.slice(0, 4)))].sort(),
): AnnualScenarioImpact[] => years.map((year) => {
  const yearMonths = months.filter((month) => month.month.startsWith(`${year}-`));
  const planRevenue = yearMonths.reduce((sum, month) => sum + month.baselineRevenue, 0);
  const revenue = yearMonths.reduce((sum, month) => sum + month.scenarioRevenue, 0);
  const planExpenses = yearMonths.reduce((sum, month) => sum + month.baselineExpenses, 0);
  const scenarioExpenses = yearMonths.reduce((sum, month) => sum + month.scenarioExpenses, 0);

  return {
    year,
    planRevenue,
    revenue,
    revenueImpact: revenue - planRevenue,
    planExpenses,
    scenarioExpenses,
    savings: planExpenses - scenarioExpenses,
    planProfit: planRevenue - planExpenses,
    scenarioProfit: revenue - scenarioExpenses,
    actualMonths: yearMonths.filter((month) => month.kind === 'actual').length,
    forecastMonths: yearMonths.filter((month) => month.kind === 'forecast').length,
  };
});
